import { Alert, Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CashOutOfBox, useCashBoxes } from '../../components/CashOutOfBox';
import { CashOutOfRegister, useOpenRegisters } from '../../components/CashOutOfRegister';
import { CrudSection } from '../../components/CrudSection';
import { EntityLabel } from '../../components/EntityField';
import { PaperRowControl } from '../../components/ThePaperItself';
import { enumOptions } from '../../lib/enums';
import { formatDate, formatMoney, type NumberInputValue, toMajorUnits, toMinorUnits } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { BillSubject, type BillSubjectFields, billSubject } from './billSubject';

const PAYABLES_PATH = '/admin/v1/suppliers/payables';
/** The vendor page's own bill list, which shares these rows and must not keep a stale one. */
const BILLS_PATH = '/admin/v1/suppliers/supplier-invoices';
const PAYMENTS_PATH = '/admin/v1/suppliers/supplier-payments';
const BANK_ACCOUNTS_PATH = '/admin/v1/admin/bank-accounts';

export interface Bill {
  id: string;
  supplierId: string;
  invoiceNumber?: string | null;
  /**
   * What the bill was FOR. Set on a bill with nothing to count behind it — the propane, the ice —
   * where it is the only thing that says which bill this is. Null on one a delivery opened, whose
   * receipt lines say it instead.
   */
  description?: string | null;
  /** Set when a delivery opened this bill — which is the answer to "what for" on those rows. */
  receiptId?: string | null;
  /** The photographed bill, when somebody took one. Null means nobody did — not that it is lost. */
  sourceDocumentId?: string | null;
  invoiceDate: string;
  dueDate?: string | null;
  currency: string;
  totalMinor: number;
  paidMinor: number;
  status: string;
  agingDays: number;
  /** What the supplier has credited back against this bill. */
  creditedMinor: number;
  /**
   * Billed, less paid, LESS CREDITED — worked out by the server, not here.
   *
   * Three screens and the assistant were each subtracting `paidMinor` from `totalMinor` in their
   * own words, and a credit note makes every one of those subtractions wrong by the amount of the
   * credit, in the direction of "you still owe this", on the list whose whole job is what you owe.
   */
  outstandingMinor: number;
  /** Billed less what arrived. Positive: they billed for more than turned up. Zero when they agree. */
  discrepancyMinor?: number;
  /** How much of that difference nobody has answered yet. */
  discrepancyOpenMinor?: number;
}

/**
 * How far above what is owed a payment may go: one whole unit, L 1.00 or $ 1.00.
 *
 * The same figure as `CASH_ROUNDING_ALLOWANCE_MINOR` in
 * `backend/src/domains/suppliers/routes.ts`, for the same reason: there are no coins on this island,
 * cash comes and goes in whole bills, and cash rounds up — so a bill for L 2,437.50 settled in notes
 * is L 2,438.00 and the fifty centavos of surplus is money that does not physically exist. Held in
 * both places on purpose, so that this screen refuses exactly the set the API refuses and the reason
 * is beside the box that caused it rather than arriving as a server error after the press.
 */
const CASH_ROUNDING_ALLOWANCE_MINOR = 100;

interface BankAccount {
  id: string;
  bankName: string;
  accountLabel: string;
  currency: string;
  active: boolean;
}

/**
 * The bills that are still open, and paying them.
 *
 * The list on its own was a list that could never get shorter: a bill opens when a delivery is
 * recorded and closes only when a payment is written against it, and there was no way to write
 * one. So the tab described payments settling bills, showed every bill as unpaid for ever, and the
 * "You owe" figure on the supplier list could only ever climb.
 */
export function PayablesSection() {
  const queryClient = useQueryClient();
  const [paying, setPaying] = useState<Bill | null>(null);

  return (
    <>
      <CrudSection
        title="Bills to pay"
        description="What you still owe each supplier, longest outstanding first. Record a payment against a bill and it comes off this list."
        listPath={PAYABLES_PATH}
        emptyMessage="Nothing outstanding. Every supplier bill is paid."
        extractList={(d) =>
          (
            d as {
              invoice: Record<string, unknown>;
              agingDays: number;
              creditedMinor: number;
              outstandingMinor: number;
              discrepancyOpenMinor: number;
            }[]
          ).map((x) => ({
            ...x.invoice,
            agingDays: x.agingDays,
            creditedMinor: x.creditedMinor,
            outstandingMinor: x.outstandingMinor,
            discrepancyOpenMinor: x.discrepancyOpenMinor,
          }))
        }
        columns={[
          { accessor: 'supplierId', title: 'Supplier', reference: 'supplier' },
          {
            accessor: 'invoiceNumber',
            title: 'What for',
            // A bill from the gas man for filling the propane tank has no invoice number and no
            // goods behind it, so a column of invoice numbers showed it as a blank row on a list
            // whose whole job is being checkable against the paperwork. Every other row owes the
            // same answer — see billSubject.tsx for why a delivery says so rather than "—".
            render: (r) => (
              <Stack gap={2} align="flex-start">
                <BillSubject bill={r as BillSubjectFields} />
                {/* The paper the figure came off, on the row where somebody is about to decide
                    whether to pay it. Also the door to attaching one: the moment a person has the
                    bill in their hand is the moment they are looking at this list, and sending
                    them somewhere else to photograph it is how it does not get photographed. */}
                <PaperRowControl
                  documentId={r.sourceDocumentId as string | null}
                  attachPath={`${BILLS_PATH}/${String(r.id)}/document`}
                  subject={billSubject(r as BillSubjectFields)}
                  invalidate={[listKey(PAYABLES_PATH), listKey(BILLS_PATH)]}
                />
              </Stack>
            ),
          },
          {
            accessor: 'dueDate',
            title: 'Due',
            noWrap: true,
            // How late it is belongs beside the date it was due, not in a column of bare numbers
            // headed "Days overdue" that a reader has to pair up with the date themselves.
            render: (r) => <DueCell dueDate={r.dueDate} invoiceDate={r.invoiceDate} days={r.agingDays} />,
          },
          {
            accessor: 'outstanding',
            title: 'Still owed',
            // The figure the screen exists for, so it carries the part-payment underneath it
            // rather than costing a column of its own that is blank on almost every row.
            render: (r) => {
              const currency = String(r.currency ?? 'HNL');
              const total = (r.totalMinor as number) ?? 0;
              const paid = (r.paidMinor as number) ?? 0;
              const credited = (r.creditedMinor as number) ?? 0;
              return (
                <Stack gap={0}>
                  <Text size="sm" fw={700}>
                    {formatMoney((r.outstandingMinor as number) ?? total - paid, currency)}
                  </Text>
                  {paid > 0 && (
                    <Text size="xs" c="dimmed">
                      {formatMoney(paid, currency)} of {formatMoney(total, currency)} paid
                    </Text>
                  )}
                  {/* Said out loud, because otherwise this row is simply smaller than the bill and
                      nobody can tell whether that is a part payment or a credit — two facts with
                      very different consequences when the vendor's statement arrives. */}
                  {credited > 0 && (
                    <Text size="xs" c="dimmed">
                      {formatMoney(credited, currency)} credited back
                    </Text>
                  )}
                  {/* The half of this figure that nobody has agreed to yet. Said HERE, on the row
                      carrying the button that pays it, because paying a bill in full while three
                      cases of it never turned up is the mistake the whole of this exists to stop —
                      and it is a mistake nobody makes if the row says so. */}
                  {typeof r.discrepancyOpenMinor === 'number' && r.discrepancyOpenMinor > 0 && (
                    <Text size="xs" c="orange.8">
                      {formatMoney(r.discrepancyOpenMinor as number, currency)} billed and not delivered
                    </Text>
                  )}
                </Stack>
              );
            },
          },
          { accessor: 'status', title: 'Status', enumGroup: 'payableStatus' },
        ]}
        rowActions={(row) => (
          <Button size="xs" variant="light" onClick={() => setPaying(row as unknown as Bill)}>
            Record a payment
          </Button>
        )}
      />

      {paying && (
        <RecordPaymentForm
          bill={paying}
          onClose={() => setPaying(null)}
          /*
           * Every list this payment appears on, not only the one it comes off.
           *
           * "Payments made" sits directly beneath this table on the same tab, and it carries the
           * only button that can take a payment back — so it is exactly where somebody goes the
           * moment they realise they typed the wrong figure. Invalidating the payables list alone
           * left it showing the rows it had before: L 2,100.00 was recorded, the toast said so, and
           * the row to undo it was not on the screen. Measured on the real screen 2026-07-29. It is
           * the defect `api/queryKeys.ts` was written about, sitting underneath the feature built
           * so that a payment could be taken back at all.
           */
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: listKey(PAYABLES_PATH) });
            void queryClient.invalidateQueries({ queryKey: listKey(PAYMENTS_PATH) });
            void queryClient.invalidateQueries({ queryKey: listKey(BILLS_PATH) });
          }}
        />
      )}
    </>
  );
}

/** The due date, and how far past it this bill is. */
function DueCell({ dueDate, invoiceDate, days }: { dueDate: unknown; invoiceDate: unknown; days: unknown }) {
  const due = typeof dueDate === 'string' ? dueDate : null;
  const late = typeof days === 'number' ? days : 0;
  return (
    <Stack gap={0}>
      <Text size="sm">
        {due ? formatDate(due) : 'On delivery'}
        {!due && typeof invoiceDate === 'string' && (
          <Text span size="xs" c="dimmed">
            {` · dated ${formatDate(invoiceDate)}`}
          </Text>
        )}
      </Text>
      {late > 0 && (
        <Text size="xs" fw={600} c={late > 30 ? 'red.7' : 'orange.8'}>
          {late} {late === 1 ? 'day' : 'days'} late
        </Text>
      )}
    </Stack>
  );
}

/**
 * Paying a bill, or part of one.
 *
 * The amount is in the money the supplier billed in and cannot be switched: what is recorded here
 * is added straight onto what the bill says has been paid, so five hundred lempiras entered against
 * a five-hundred-dollar invoice would mark it settled and take a bill worth twenty-six times more
 * off the list.
 */
export function RecordPaymentForm({
  bill,
  onClose,
  onSaved,
}: {
  bill: Bill;
  onClose: () => void;
  onSaved: () => void;
}) {
  // What is actually left on it: billed, less paid, LESS anything the supplier credited back. A
  // bill they credited in half was offering to pay them twice what is owed, with the box filled in.
  const owed = bill.outstandingMinor ?? bill.totalMinor - bill.paidMinor;
  const [amount, setAmount] = useState<NumberInputValue>(toMajorUnits(owed));
  const [method, setMethod] = useState('cashDrawer');
  const [bankAccountId, setBankAccountId] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [drawerSessionId, setDrawerSessionId] = useState<string | null>(null);

  /*
   * WHICH DRAWER THE NOTES CAME OUT OF, asked before the money is recorded.
   *
   * Paying a bill "cash out of the register" now takes the cash out of the register: the server records a
   * paid-out against this drawer and its expected balance comes down. Until it did, whoever counted
   * that drawer off at one in the morning came up short by exactly this amount.
   */
  const cash = method === 'cashDrawer';
  const { registers, loading: registersLoading, noneOpen } = useOpenRegisters(cash);
  // One open register is the ordinary evening at this bar and needs no question answered twice; the
  // picker still shows which one, because "the drawer that will be short tonight" is worth reading.
  const chosenRegister =
    drawerSessionId ?? (registers.length === 1 ? (registers[0]?.drawerSessionId ?? null) : null);

  /*
   * WHICH BOX, for the payment no register is open for — the water man at eleven in the morning, paid
   * out of the change box the owner keeps stocked for exactly this. The server records the movement
   * against the box itself and its derived balance comes down; no drawer is touched. Until this
   * method existed the honest answer was "Some other way", which named no container at all — the
   * cash left the box and nothing anywhere said so.
   */
  const fromBox = method === 'cashBox';
  const { boxes, loading: boxesLoading, none: noBoxes } = useCashBoxes(fromBox);
  const [cashBoxId, setCashBoxId] = useState<string | null>(null);
  // The single-box site skips the question the same way the single-register one does; the picker still
  // names it, because which box is about to be lighter is worth reading before the press.
  const chosenBox = cashBoxId ?? (boxes.length === 1 ? (boxes[0]?.id ?? null) : null);

  const accounts = useQuery({
    queryKey: listKey(BANK_ACCOUNTS_PATH),
    queryFn: () => rawApi<BankAccount[]>('GET', BANK_ACCOUNTS_PATH),
    enabled: method === 'bankTransfer',
    staleTime: 60_000,
  });
  // Only accounts held in the money this bill is in. A lempira account against a dollar invoice is
  // a reconciliation nobody can finish, and the account is only being named to make that possible.
  const usable = (accounts.data ?? []).filter((a) => a.active && a.currency === bill.currency);

  const amountMinor = toMinorUnits(amount) ?? 0;
  /*
   * Two bands, not one, because there are two different things a bigger number means.
   *
   * Rounding a bill up to the notes actually handed over is real and has to be recordable — the
   * money leaving the register is the money that left the register. A typed extra zero is not: $99,999.99
   * against a $150 bill used to be accepted by both this screen and the API, and was found a month
   * later when the supplier's statement disagreed and nobody could say which figure was wrong.
   */
  const overBy = amountMinor - owed;
  const rounding = overBy > 0 && overBy <= CASH_ROUNDING_ALLOWANCE_MINOR;
  const tooMuch = overBy > CASH_ROUNDING_ALLOWANCE_MINOR;
  const subject = billSubject(bill);

  const pay = useMutation({
    mutationFn: () =>
      rawApi('POST', PAYMENTS_PATH, {
        supplierInvoiceId: bill.id,
        amountMinor,
        currency: bill.currency,
        method,
        drawerSessionId: cash ? chosenRegister : null,
        cashLocationId: fromBox ? chosenBox : null,
        bankAccountId: method === 'bankTransfer' ? bankAccountId : null,
        reference: reference.trim() || null,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
      const left = owed - amountMinor;
      const stillOwed =
        left > 0
          ? `${formatMoney(left, bill.currency)} still owed on this bill.`
          : 'This bill is settled and is off the list.';
      notifyDone(
        'Payment recorded',
        // Said out loud, because it is the half of this gesture nobody could see before: the money
        // has come off the register's expectation — or off the named box's own ledger — so the
        // container it left still accounts for it.
        cash
          ? `${stillOwed} It has come out of the register, so the drawer still counts off level.`
          : fromBox
            ? `${stillOwed} It has come out of the ${
                boxes.find((b) => b.id === chosenBox)?.name ?? 'box'
              }, on that box's own ledger — no drawer is touched.`
            : stillOwed,
      );
    },
    onError: (err: Error) => notifyError(err, 'record this payment'),
  });

  const submit = () => {
    if (amountMinor <= 0) return setError('Say how much was paid');
    if (tooMuch) return setError('That is far more than this bill is for — check the amount');
    if (cash && !chosenRegister) return setError('Say which register the cash came out of');
    if (fromBox && !chosenBox) return setError('Say which box the cash came out of');
    setError(null);
    pay.mutate();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title="Record a payment"
      size="lg"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {/* Which bill this is, not just whose. An invoice number used to be the only thing named
            here, and island vendors mostly do not give one — so a vendor with two propane bills
            open showed the same sentence twice and the owner had no way to tell which of them he
            was about to settle. */}
        <Group gap={6} wrap="wrap">
          <EntityLabel entity="supplier" id={bill.supplierId} />
          <Text size="sm" c="dimmed">
            {subject === '—' ? '' : `${subject} · `}
            {formatMoney(owed, bill.currency)} outstanding
          </Text>
        </Group>

        <NumberInput
          label={`How much you paid, in ${bill.currency === 'USD' ? 'dollars' : 'lempiras'}`}
          description="Leave it as it is to settle the bill, or lower it to record a part payment"
          required
          min={0}
          prefix={bill.currency === 'USD' ? '$ ' : 'L '}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          hideControls
          value={amount}
          // Kept exactly as the box hands it back — see `NumberInputValue` in lib/format.
          onChange={setAmount}
          disabled={pay.isPending}
        />

        <Select
          label="How you paid it"
          required
          // Off the shared registry, so the payments list reads back exactly the words this form
          // offered — the two had drifted into being written twice before the registry existed.
          data={enumOptions('supplierPaymentMethod')}
          value={method}
          onChange={(v) => setMethod(v ?? 'cashDrawer')}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
          disabled={pay.isPending}
        />

        {cash && (
          <CashOutOfRegister
            registers={registers}
            loading={registersLoading}
            value={chosenRegister}
            onChange={setDrawerSessionId}
            currency={bill.currency}
            amountMinor={amountMinor}
            disabled={pay.isPending}
            // This form has the real from-the-box method, so the no-drawer alert may name it.
            boxOffered
          />
        )}

        {fromBox && (
          <CashOutOfBox
            boxes={boxes}
            loading={boxesLoading}
            value={chosenBox}
            onChange={setCashBoxId}
            currency={bill.currency}
            amountMinor={amountMinor}
            disabled={pay.isPending}
          />
        )}

        {method === 'bankTransfer' && (
          <Select
            label="Out of which account"
            description={
              usable.length === 0 && !accounts.isLoading
                ? `No account on file is held in ${bill.currency === 'USD' ? 'dollars' : 'lempiras'} — the payment still records without one`
                : 'So the transfer can be matched on the statement'
            }
            placeholder={accounts.isLoading ? 'Loading…' : 'Not saying'}
            data={usable.map((a) => ({ value: a.id, label: `${a.bankName} · ${a.accountLabel}` }))}
            value={bankAccountId}
            onChange={setBankAccountId}
            clearable
            comboboxProps={{ withinPortal: true }}
            disabled={pay.isPending}
          />
        )}

        <TextInput
          label="Reference"
          description="Transfer number, or whatever you wrote on the receipt"
          value={reference}
          onChange={(e) => setReference(e.currentTarget.value)}
          disabled={pay.isPending}
        />

        {/* Paying more than is owed is how a typed extra zero gets discovered a month later, when
            the supplier statement and this one disagree and nobody can say which is right. */}
        {rounding && (
          <Alert color="yellow" variant="light" title="That is more than this bill is for">
            {formatMoney(overBy, bill.currency)} more than the {formatMoney(owed, bill.currency)} outstanding
            — about what rounding up to the notes you handed over would come to. Record it if that is
            genuinely what you paid them.
          </Alert>
        )}
        {tooMuch && (
          <Alert color="red" variant="light" title="That is far more than this bill is for">
            {formatMoney(amountMinor, bill.currency)} against a bill with {formatMoney(owed, bill.currency)}{' '}
            outstanding. Check the amount — a payment this far over is refused.
          </Alert>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={pay.isPending}>
            Cancel
          </Button>
          {/* Blocked rather than allowed-and-refused, so the reason is on the screen beside the
              choice that caused it instead of arriving as a server error after the press. */}
          <Button
            loading={pay.isPending}
            onClick={submit}
            disabled={tooMuch || (cash && noneOpen) || (fromBox && noBoxes)}
          >
            Record the payment
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
